import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Paper, Box } from '@mui/material';
import {
  formatRupeesIndianCompact,
  premiumTooltip,
  horizontalGrid,
  indianValueAxis,
  indianCategoryAxis,
  defaultAnimation,
} from './echartsConfig';
import { reportChartPaperSx } from './reportChartLayout';
import ReportCardHeader, { ReportInsightBanner } from './ReportCardHeader';
import ReportKpiStrip, { kpiMapToItems } from './ReportKpiStrip';

const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const norm = s => String(s || '').toLowerCase().trim();

const CONFIRMED_GREEN = '#66BB6A';

function prepareApcDisplay(data) {
  const rawLabels = (data?.labels || []).map(l => String(l ?? ''));
  const rawDatasets = data?.datasets || [];

  const confIdx = rawDatasets.findIndex(ds => norm(ds.label).includes('confirm'));
  const confDs = confIdx >= 0 ? rawDatasets[confIdx] : rawDatasets[0];
  if (!confDs) {
    return { labels: [], values: [], highestSegment: '—', lowestSegment: '—', overallApc: 0 };
  }

  const confData = (confDs.data || []).map(toNum);

  const rows = rawLabels
    .map((label, i) => ({ label, value: confData[i] || 0 }))
    .filter(r => r.value > 0)
    .sort((a, b) => b.value - a.value);

  if (!rows.length) {
    return { labels: [], values: [], highestSegment: '—', lowestSegment: '—', overallApc: 0 };
  }

  const labels = rows.map(r => r.label);
  const values = rows.map(r => r.value);
  const total = values.reduce((s, v) => s + v, 0);
  const overallApc = values.length > 0 ? total / values.length : 0;

  return {
    labels,
    values,
    highestSegment: labels[0],
    highestValue: values[0],
    lowestSegment: labels[labels.length - 1],
    lowestValue: values[values.length - 1],
    overallApc,
  };
}

const SegmentApcChart = ({ data, name, handleExport, showButton = true }) => {
  const displayName = data?.name || name;

  const prepared = useMemo(() => prepareApcDisplay(data), [data]);

  const plotHeight = useMemo(
    () => Math.min(440, Math.max(200, prepared.labels.length * 32)),
    [prepared.labels.length],
  );

  const option = useMemo(() => ({
    grid: horizontalGrid,
    tooltip: {
      ...premiumTooltip,
      formatter(params) {
        const p = Array.isArray(params) ? params[0] : params;
        if (!p) return '';
        return `<div style="font-weight:600;font-size:13px;color:#f8fafc;margin-bottom:4px">${p.name}</div>`
          + `<div style="font-variant-numeric:tabular-nums">APC: ${formatRupeesIndianCompact(p.value)}</div>`;
      },
    },
    xAxis: {
      ...indianValueAxis,
      position: 'bottom',
    },
    yAxis: {
      ...indianCategoryAxis,
      data: prepared.labels,
      inverse: true,
    },
    series: [
      {
        type: 'bar',
        data: prepared.values,
        barWidth: '55%',
        itemStyle: {
          color: CONFIRMED_GREEN,
          borderRadius: [0, 3, 3, 0],
        },
        emphasis: { itemStyle: { shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.15)' } },
      },
    ],
    ...defaultAnimation,
  }), [prepared]);

  const apiKpis = kpiMapToItems(data?.kpiMap);
  const infoTooltip = data?.infoText || 'Confirmed avg revenue per cover by segment — ranked highest to lowest';

  return (
    <Paper sx={reportChartPaperSx}>
      <ReportCardHeader
        title={displayName}
        insight={infoTooltip}
        handleExport={handleExport}
        showButton={showButton}
      />

      <ReportKpiStrip
        columns={2}
        items={apiKpis || [
          { label: 'Highest APC Segment', value: `${prepared.highestSegment} (${formatRupeesIndianCompact(prepared.highestValue || 0)})` },
          { label: 'Overall APC', value: formatRupeesIndianCompact(prepared.overallApc) },
        ]}
      />

      <ReportInsightBanner text={data?.insightText} style={data?.insightStyle} />

      <Box sx={{ width: '100%', height: plotHeight }}>
        <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
      </Box>
    </Paper>
  );
};

export default SegmentApcChart;
